import React from 'react';
import Footer from '../components/Footer';
import Header from '../components/Header';
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiChevronDown, FiCheck, FiDollarSign, FiHome, FiPieChart, FiTrendingUp, FiShield, FiCalendar } from 'react-icons/fi';
import styled from 'styled-components';

// Styled components for improved alignment and design
const SectionWrapper = styled.section`
  padding: 80px 0;
  background-color: #fafafa;
  min-height: 100vh;
`;

const ContentContainer = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 20px;
  display: grid;
  gap: 40px;
`;

const Heading = styled.h2`
  color: var(--theme-color);
  font-size: 2.25rem;
  font-weight: 600;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 12px;
  line-height: 1.3;
`;

const SubHeading = styled.h3`
  color: #2d2d2d;
  font-size: 1.75rem;
  font-weight: 500;
  margin-bottom: 15px;
`;

const Paragraph = styled.p`
  color: #4a4a4a;
  font-size: 1.1rem;
  line-height: 1.8;
  margin-bottom: 20px;
`;

const ListItem = styled.li`
  margin-bottom: 12px;
  list-style: none;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 1.1rem;
  color: #4a4a4a;
  &::before {
    content: '•';
    color: var(--theme-color);
    font-size: 1.5rem;
    line-height: 1;
  }
`;

const OrderedList = styled.ol`
  padding-left: 20px;
  margin-bottom: 20px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 30px;
  background-color: #fff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  border-radius: 8px;
  overflow: hidden;
  th, td {
    border: 1px solid #e0e0e0;
    padding: 14px;
    text-align: left;
    font-size: 1rem;
  }
  th {
    background-color: var(--theme-color);
    color: white;
    font-weight: 600;
  }
  td {
    color: #4a4a4a;
  }
`;

const AccordionButton = styled.button`
  width: 100%;
  text-align: left;
  padding: 18px;
  background-color: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  margin-bottom: 10px;
  font-size: 1.25rem;
  font-weight: 500;
  color: #2d2d2d;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  transition: background-color 0.3s ease, transform 0.2s ease;
  &:hover {
    background-color: #f5f5f5;
    transform: translateY(-2px);
  }
`;

const AccordionContent = styled(motion.div)`
  padding: 20px;
  background-color: #fff;
  border: 1px solid #e0e0e0;
  border-top: none;
  border-radius: 0 0 6px 6px;
  margin-bottom: 20px;
`;

const ContentBlock = styled(motion.div)`
  background-color: #fff;
  padding: 30px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  margin-bottom: 30px;
`;

const TabBar = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 40px;
  border-bottom: 1px solid #e0e0e0;
  flex-wrap: wrap;
`;

const TabButton = styled.button`
  padding: 12px 24px;
  background: none;
  border: none;
  border-bottom: 3px solid transparent;
  font-size: 16px;
  font-weight: 600;
  color: #666;
  cursor: pointer;
  transition: all 0.3s ease;
  &:hover, &.active {
    color: var(--theme-color);
    border-bottom-color: var(--theme-color);
  }
`;

const ContentImage = styled.img`
  max-width: 100%;
  width: 100%;
  max-width: 560px;
  height: auto;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  margin: 20px 0;
  display: block;
`;

export default function Financing() {
    const [isLoading, setIsLoading] = useState(true);
    const [activeSection, setActiveSection] = useState('financing');
    const [expandedSections, setExpandedSections] = useState({});

    useEffect(() => {
        setTimeout(() => {
            setIsLoading(false);
        }, 1000);
    }, []);

    const toggleSection = (sectionId) => {
        setExpandedSections(prev => ({
            ...prev,
            [sectionId]: !prev[sectionId]
        }));
    };

    const fadeIn = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
    };

    const sectionVariants = {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { staggerChildren: 0.2 } }
    };

    return (
        <div className="real-estate-page">
            <style jsx>{`
                :root {
                    --theme-color: #f7bf68;
                }
                .real-estate-page {
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                    color: #2d2d2d;
                    line-height: 1.6;
                    background-color: #f5f5f5;
                }
            `}</style>

            <div className="boxed_wrapper ltr">
                {isLoading && (
                    <div className="loader-wrap">
                        <div className="preloader">
                            <div className="preloader-close">close</div>
                            <div id="handle-preloader" className="handle-preloader">
                                <div className="animation-preloader">
                                    <div className="txt-loading">
                                        <img src="assets/images/animation_logo.gif" alt="Loading" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <Header />

                <section className="page-title">
                    <div
                        className="bg-layer"
                        style={{
                            backgroundImage: `url("/assets/images/background/page-title.jpg")`,
                        }}
                    ></div>
                    <div className="line-grid-wrap">
                        <span className="line-grid-1"></span>
                        <span className="line-grid-2"></span>
                        <span className="line-grid-3"></span>
                        <span className="line-grid-4"></span>
                        <span className="line-grid-5"></span>
                    </div>
                    <div className="auto-container">
                        <div className="content-box">
                            <h1 style={{ textAlign: "left" }}>Financing</h1>
                            <ul className="bread-crumb clearfix">
                                <li>
                                    <Link to="/">Home</Link>
                                </li>
                                <li>Financing</li>
                            </ul>
                        </div>
                    </div>
                </section>

                <SectionWrapper className="real-estate-content">
                    <ContentContainer>
                        <TabBar>
                            <TabButton className={activeSection === 'financing' ? 'active' : ''} onClick={() => setActiveSection('financing')}>Financing</TabButton>
                            <TabButton className={activeSection === 'tax-strategies' ? 'active' : ''} onClick={() => setActiveSection('tax-strategies')}>Tax Strategies</TabButton>
                        </TabBar>

                        {activeSection === 'financing' && (
                        <motion.div variants={sectionVariants} initial="hidden" animate="visible">
                            {/* Page 1 Content */}
                            <ContentBlock variants={fadeIn}>
                                <Heading><FiDollarSign /> Ravline Corp Financial Services</Heading>
                                <Paragraph>Finance is the management of money and monetary resources for individuals, businesses, and governments. It includes activities such as saving, investing, borrowing, and lending — all with the goal of creating value, managing risk, and building a stable financial future.</Paragraph>
                            </ContentBlock>

                            <ContentBlock variants={fadeIn}>
                                <SubHeading>Key Aspects of Finance</SubHeading>
                                <Table>
                                    <thead>
                                        <tr>
                                            <th>Aspect</th>
                                            <th>Description</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td>Money Management</td>
                                            <td>Creating, moving, allocating money effectively.</td>
                                        </tr>
                                        <tr>
                                            <td>Risk & Return</td>
                                            <td>Balancing the potential for profit with acceptable risk.</td>
                                        </tr>
                                        <tr>
                                            <td>Time Value of Money</td>
                                            <td>Recognizing that money now is worth more than the same amount later due to its earning potential.</td>
                                        </tr>
                                    </tbody>
                                </Table>
                            </ContentBlock>

                            <ContentBlock variants={fadeIn}>
                                <SubHeading>The 3 Pillars of Finance</SubHeading>
                                <OrderedList>
                                    <ListItem>Personal Finance - Managing income, debt, saving, investing, and retirement at the individual or household level.</ListItem>
                                    <ListItem>Corporate Finance - How companies raise and manage capital, assess projects, and drive shareholder value.</ListItem>
                                    <ListItem>Public Finance - How governments fund services, manage debt, and stimulate economic stability through taxation and public spending.</ListItem>
                                </OrderedList>
                            </ContentBlock>

                            {/* Page 2 Content */}
                            <ContentBlock variants={fadeIn}>
                                <Heading><FiTrendingUp /> Why Financing Matters</Heading>
                                <ul>
                                    <ListItem>Operational Efficiency: Enables businesses and governments to fund their operations and scale.</ListItem>
                                    <ListItem>Goal Achievement: Helps individuals and companies reach specific milestones through planned financial decision-making.</ListItem>
                                    <ListItem>Economic Circulation: Drives the flow of credit, investment, and consumer spending — fueling economic activity at all levels.</ListItem>
                                </ul>
                            </ContentBlock>

                            <ContentBlock variants={fadeIn}>
                                <SubHeading>Types of Financing You Should Know</SubHeading>
                                <Paragraph>Let’s break down the main categories of financing you or your business may consider:</Paragraph>
                            </ContentBlock>

                            {/* SBA Lending */}
                            <AccordionButton onClick={() => toggleSection('sba')}>
                                1. SBA Lending (Small Business Administration) <FiChevronDown />
                            </AccordionButton>
                            {expandedSections['sba'] && (
                                <AccordionContent variants={fadeIn}>
                                    <Paragraph>SBA loans are among the best resources for small businesses due to their low rates and favorable terms — but they come with strict underwriting guidelines.</Paragraph>
                                    <Table>
                                        <thead>
                                            <tr>
                                                <th>SBA Loan Type</th>
                                                <th>Purpose</th>
                                                <th>Loan Amount</th>
                                                <th>Term</th>
                                                <th>Typical Rate</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td>7(a)</td>
                                                <td>Working capital, acquisitions</td>
                                                <td>Up to $5M</td>
                                                <td>10–25 years</td>
                                                <td>8%–11%</td>
                                            </tr>
                                            <tr>
                                                <td>504</td>
                                                <td>Equipment or real estate</td>
                                                <td>Up to $5.5M</td>
                                                <td>10–25 years</td>
                                                <td>Market-based</td>
                                            </tr>
                                            <tr>
                                                <td>Microloan</td>
                                                <td>Startup or small expansion</td>
                                                <td>Up to $50K</td>
                                                <td>Up to 6 years</td>
                                                <td>6%–9%</td>
                                            </tr>
                                        </tbody>
                                    </Table>
                                    <Paragraph>Best Practice: Work closely with a business consultant and accountant to prepare a bulletproof application.</Paragraph>
                                </AccordionContent>
                            )}

                            {/* Business Lines of Credit */}
                            <AccordionButton onClick={() => toggleSection('linesOfCredit')}>
                                2. Business Lines of Credit <FiChevronDown />
                            </AccordionButton>
                            {expandedSections['linesOfCredit'] && (
                                <AccordionContent variants={fadeIn}>
                                    <Paragraph>A revolving credit line used for inventory, payroll, marketing, or emergencies.</Paragraph>
                                    <ul>
                                        <ListItem>Limits: $10K to $250K+</ListItem>
                                        <ListItem>Interest only on the amount used</ListItem>
                                        <ListItem>Flexible, short-term access to capital</ListItem>
                                    </ul>
                                </AccordionContent>
                            )}

                            {/* Commercial Lending */}
                            <AccordionButton onClick={() => toggleSection('commercial')}>
                                3. Commercial Lending <FiChevronDown />
                            </AccordionButton>
                            {expandedSections['commercial'] && (
                                <AccordionContent variants={fadeIn}>
                                    <Paragraph>Used for purchasing commercial property, expansion, equipment, or increasing working capital.</Paragraph>
                                    <Paragraph>Underwritten based on:</Paragraph>
                                    <ul>
                                        <ListItem>Cash flow</ListItem>
                                        <ListItem>Collateral</ListItem>
                                        <ListItem>Industry risk</ListItem>
                                        <ListItem>Time in business</ListItem>
                                    </ul>
                                </AccordionContent>
                            )}

                            {/* DSCR Lending */}
                            <AccordionButton onClick={() => toggleSection('dscr')}>
                                4. DSCR Lending (Debt Service Coverage Ratio) <FiChevronDown />
                            </AccordionButton>
                            {expandedSections['dscr'] && (
                                <AccordionContent variants={fadeIn}>
                                    <Paragraph>Used primarily by real estate investors. Measures if income from an asset can cover loan payments.</Paragraph>
                                    <Paragraph>Formula: DSCR = Net Operating Income / Debt Payments</Paragraph>
                                    <Paragraph>Example: Rental income: $4,000/month, Mortgage payment: $3,000/month, DSCR = 1.33</Paragraph>
                                    <ul>
                                        <ListItem>No W2s or tax returns needed</ListItem>
                                        <ListItem>Higher interest rates apply</ListItem>
                                    </ul>
                                </AccordionContent>
                            )}

                            {/* Hard Equity Loans */}
                            <AccordionButton onClick={() => toggleSection('hardEquity')}>
                                5. Hard Equity Loans (Hard Money) <FiChevronDown />
                            </AccordionButton>
                            {expandedSections['hardEquity'] && (
                                <AccordionContent variants={fadeIn}>
                                    <Paragraph>Short-term, asset-backed loans ideal for flippers or businesses with poor credit.</Paragraph>
                                    <Table>
                                        <thead>
                                            <tr>
                                                <th>Feature</th>
                                                <th>Range</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td>Approval</td>
                                                <td>3–7 days</td>
                                            </tr>
                                            <tr>
                                                <td>Term</td>
                                                <td>6–24 months</td>
                                            </tr>
                                            <tr>
                                                <td>Interest</td>
                                                <td>9%–15%</td>
                                            </tr>
                                            <tr>
                                                <td>Loan-to-Value</td>
                                                <td>60%–75%</td>
                                            </tr>
                                        </tbody>
                                    </Table>
                                    <Paragraph>Use as a bridge loan — not a long-term strategy.</Paragraph>
                                </AccordionContent>
                            )}

                            {/* Mortgage Lending */}
                            <AccordionButton onClick={() => toggleSection('mortgage')}>
                                6. Mortgage Lending (Residential & Commercial) <FiChevronDown />
                            </AccordionButton>
                            {expandedSections['mortgage'] && (
                                <AccordionContent variants={fadeIn}>
                                    <Table>
                                        <thead>
                                            <tr>
                                                <th>Loan Type</th>
                                                <th>Best For</th>
                                                <th>Credit Needed</th>
                                                <th>Down Payment</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td>Conventional</td>
                                                <td>Most buyers</td>
                                                <td>620+</td>
                                                <td>3%–20%</td>
                                            </tr>
                                            <tr>
                                                <td>FHA</td>
                                                <td>First-time buyers</td>
                                                <td>580+</td>
                                                <td>3.5%</td>
                                            </tr>
                                            <tr>
                                                <td>VA</td>
                                                <td>Veterans</td>
                                                <td>580+</td>
                                                <td>0%</td>
                                            </tr>
                                            <tr>
                                                <td>DSCR</td>
                                                <td>Investors</td>
                                                <td>620+</td>
                                                <td>20%–30%</td>
                                            </tr>
                                            <tr>
                                                <td>Non-QM</td>
                                                <td>Self-employed</td>
                                                <td>Varies</td>
                                                <td>10%–30%</td>
                                            </tr>
                                        </tbody>
                                    </Table>
                                </AccordionContent>
                            )}

                            {/* Why Every Financing Decision Must Involve an Accountant */}
                            <ContentBlock variants={fadeIn}>
                                <Heading><FiShield /> Why Every Financing Decision Must Involve an Accountant</Heading>
                                <Table>
                                    <thead>
                                        <tr>
                                            <th>Factor Affected</th>
                                            <th>Reason</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td>Tax Liability</td>
                                            <td>Deduct interest, manage capital expenses</td>
                                        </tr>
                                        <tr>
                                            <td>Cash Flow</td>
                                            <td>Determine repayment schedules</td>
                                        </tr>
                                        <tr>
                                            <td>Debt-to-Income Ratio</td>
                                            <td>Affects future loan approvals</td>
                                        </tr>
                                        <tr>
                                            <td>Compliance</td>
                                            <td>With IRS and lender terms</td>
                                        </tr>
                                    </tbody>
                                </Table>
                                <Paragraph>Real Example: A $150,000 business loan at 10% = $7,500/year in interest — potentially deductible if structured correctly.</Paragraph>
                            </ContentBlock>

                            {/* Financing Tool Comparison Chart */}
                            <ContentBlock variants={fadeIn}>
                                <Heading><FiPieChart /> Financing Tool Comparison Chart</Heading>
                                <Table>
                                    <thead>
                                        <tr>
                                            <th>Financing Type</th>
                                            <th>Use Case</th>
                                            <th>Credit Needed</th>
                                            <th>Tax Benefit</th>
                                            <th>Flexibility</th>
                                            <th>Speed</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td>SBA 7(a) Loan</td>
                                            <td>Expansion / Working Cap</td>
                                            <td>Good+</td>
                                            <td></td>
                                            <td>Medium</td>
                                            <td>30–90 days</td>
                                        </tr>
                                        <tr>
                                            <td>Line of Credit</td>
                                            <td>Short-Term Expenses</td>
                                            <td>Fair+</td>
                                            <td></td>
                                            <td>High</td>
                                            <td>7–21 days</td>
                                        </tr>
                                        <tr>
                                            <td>DSCR Loan</td>
                                            <td>Investment Property</td>
                                            <td>Fair+</td>
                                            <td>(depreciation)</td>
                                            <td>Medium</td>
                                            <td>14–30 days</td>
                                        </tr>
                                        <tr>
                                            <td>Hard Equity</td>
                                            <td>Bridge Funding</td>
                                            <td>Any</td>
                                            <td></td>
                                            <td>Low</td>
                                            <td>3–7 days</td>
                                        </tr>
                                    </tbody>
                                </Table>
                            </ContentBlock>

                            {/* Conclusion */}
                            <ContentBlock variants={fadeIn}>
                                <Heading><FiCheck /> Conclusion: Tailored Financing for Real Growth</Heading>
                                <Paragraph>Whether you're starting a business, acquiring investment property, or expanding operations — financing is a critical piece of your success puzzle.</Paragraph>
                                <Paragraph>At Ravline Corp, we specialize in:</Paragraph>
                                <ul>
                                    <ListItem>Business Consulting & Strategic Lending</ListItem>
                                    <ListItem>SBA & Commercial Loan Packaging</ListItem>
                                    <ListItem>Real Estate & DSCR Financing</ListItem>
                                    <ListItem>Capital Readiness & Forecasting</ListItem>
                                    <ListItem>Integrated Tax Planning with CPAs</ListItem>
                                </ul>
                                <Paragraph>Best Practice: Align every financing decision with your accountant, tax strategist, and financial advisor to maximize value and minimize risk.</Paragraph>
                            </ContentBlock>

                            {/* Page 7 Content */}
                            <ContentBlock variants={fadeIn}>
                                <Heading><FiHome /> Financing Services: A Comprehensive Guide to Strategic Capital</Heading>
                                <Paragraph>At Ravline Corp, we believe financing is more than just borrowing money — it's about aligning capital with purpose, protection, and profitability.</Paragraph>
                            </ContentBlock>

                            <ContentBlock variants={fadeIn}>
                                <SubHeading>What Is Financing?</SubHeading>
                                <Paragraph>Financing is the process of raising money to fund operations, acquisitions, investments, or personal expenses. It involves:</Paragraph>
                                <ul>
                                    <ListItem>Managing resources over time</ListItem>
                                    <ListItem>Understanding risk vs. return</ListItem>
                                    <ListItem>Maximizing the time value of money (TVM)</ListItem>
                                </ul>
                                <Paragraph>Time Value of Money (TVM): A dollar today is worth more than a dollar tomorrow because it can be invested and earn interest.</Paragraph>
                            </ContentBlock>

                            <ContentBlock variants={fadeIn}>
                                <SubHeading>The Three Pillars of Finance</SubHeading>
                                <OrderedList>
                                    <ListItem>Personal Finance – Budgeting, saving, debt management, and retirement planning for individuals and families.</ListItem>
                                    <ListItem>Corporate Finance – Business capital planning, valuation, risk management, and shareholder wealth maximization.</ListItem>
                                    <ListItem>Public Finance – Government-level budgeting, infrastructure funding, and fiscal management.</ListItem>
                                </OrderedList>
                            </ContentBlock>

                            <ContentBlock variants={fadeIn}>
                                <SubHeading>Key Principles in Financial Decision-Making</SubHeading>
                                <ul>
                                    <ListItem>Risk vs. Return: Higher potential return usually comes with higher risk.</ListItem>
                                    <ListItem>Leverage: Using borrowed funds (debt) to increase potential returns.</ListItem>
                                    <ListItem>Cost of Capital: Weighted average cost of funds (debt + equity), which must be managed carefully to maximize value.</ListItem>
                                </ul>
                                <Paragraph>WACC Formula: WACC = (E/V × Re) + (D/V × Rd × (1 – Tc))</Paragraph>
                            </ContentBlock>

                            <ContentBlock variants={fadeIn}>
                                <SubHeading>Types of Financing Explained</SubHeading>
                                <Paragraph>Let’s explore the types of financing with real-world applications and supporting visuals for clarity.</Paragraph>
                            </ContentBlock>

                            {/* Debt Financing */}
                            <AccordionButton onClick={() => toggleSection('debt')}>
                                Debt Financing <FiChevronDown />
                            </AccordionButton>
                            {expandedSections['debt'] && (
                                <AccordionContent variants={fadeIn}>
                                    <Paragraph>Definition: Borrowed money that must be repaid with interest. Used by: Individuals, small businesses, and large corporations.</Paragraph>
                                    <Table>
                                        <thead>
                                            <tr>
                                                <th>Type</th>
                                                <th>Description</th>
                                                <th>Pros</th>
                                                <th>Cons</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td>Bank Loans</td>
                                                <td>Term-based loans with fixed interest</td>
                                                <td>Predictable payments, tax deductible</td>
                                                <td>Qualification requirements</td>
                                            </tr>
                                            <tr>
                                                <td>Lines of Credit</td>
                                                <td>Revolving credit lines</td>
                                                <td>Flexible access</td>
                                                <td>Variable interest rates</td>
                                            </tr>
                                            <tr>
                                                <td>SBA Loans</td>
                                                <td>Government-backed loans</td>
                                                <td>Favorable terms, lower down payments</td>
                                                <td>Longer approval timelines</td>
                                            </tr>
                                            <tr>
                                                <td>DSCR Loans</td>
                                                <td>Investment property loans based on income</td>
                                                <td>No personal income required</td>
                                                <td>Higher rates and LTV limits</td>
                                            </tr>
                                        </tbody>
                                    </Table>
                                    <Paragraph>Case Study: A business borrows $100,000 at 8% for 5 years. Annual interest: $8,000 Tax deduction: $8,000 × 21% tax rate = $1,680 in tax savings.</Paragraph>
                                </AccordionContent>
                            )}

                            {/* Equity Financing */}
                            <AccordionButton onClick={() => toggleSection('equity')}>
                                Equity Financing <FiChevronDown />
                            </AccordionButton>
                            {expandedSections['equity'] && (
                                <AccordionContent variants={fadeIn}>
                                    <Paragraph>Definition: Selling shares of your business in exchange for capital. Used by: Startups, high-growth companies, and innovators.</Paragraph>
                                    <Table>
                                        <thead>
                                            <tr>
                                                <th>Investor Type</th>
                                                <th>Capital Provided</th>
                                                <th>Ownership Taken</th>
                                                <th>Notes</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td>Angel Investor</td>
                                                <td>$25K–$250K</td>
                                                <td>10%–30%</td>
                                                <td>Ideal for early-stage startups</td>
                                            </tr>
                                            <tr>
                                                <td>Venture Capital</td>
                                                <td>$1M+</td>
                                                <td>25%–50%+</td>
                                                <td>Demands scalability and exit strategy</td>
                                            </tr>
                                            <tr>
                                                <td>IPO</td>
                                                <td>$10M+</td>
                                                <td>Public ownership</td>
                                                <td>Available to mature, large companies</td>
                                            </tr>
                                        </tbody>
                                    </Table>
                                    <Paragraph>Real Example: You sell 25% of your business for $50,000. If profits grow to $200,000/year, your investor now receives $50,000 annually — forever — unless you buy them out.</Paragraph>
                                </AccordionContent>
                            )}

                            {/* Alternative Financing */}
                            <AccordionButton onClick={() => toggleSection('alternative')}>
                                Alternative Financing <FiChevronDown />
                            </AccordionButton>
                            {expandedSections['alternative'] && (
                                <AccordionContent variants={fadeIn}>
                                    <Table>
                                        <thead>
                                            <tr>
                                                <th>Type</th>
                                                <th>Description</th>
                                                <th>Pros</th>
                                                <th>Cons</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td>Crowdfunding</td>
                                                <td>Raise funds from the public via online sites</td>
                                                <td>No repayment, market validation</td>
                                                <td>Requires audience building</td>
                                            </tr>
                                            <tr>
                                                <td>Grants</td>
                                                <td>Non-repayable government or foundation funds</td>
                                                <td>Free money</td>
                                                <td>Competitive, limited scope</td>
                                            </tr>
                                            <tr>
                                                <td>Factoring</td>
                                                <td>Sell invoices to receive upfront cash</td>
                                                <td>Improves cash flow</td>
                                                <td>Loss of revenue (discounted)</td>
                                            </tr>
                                            <tr>
                                                <td>Trade Credit</td>
                                                <td>Supplier allows deferred payment</td>
                                                <td>Easy access</td>
                                                <td>Short-term only</td>
                                            </tr>
                                        </tbody>
                                    </Table>
                                </AccordionContent>
                            )}

                            {/* Why Smart Financing Requires a Strategic Framework */}
                            <ContentBlock variants={fadeIn}>
                                <Heading><FiCalendar /> Why Smart Financing Requires a Strategic Framework</Heading>
                                <Paragraph>At Ravline, we emphasize accounting and tax planning integration. Here's why:</Paragraph>
                                <ul>
                                    <ListItem>Debt interest is tax-deductible</ListItem>
                                    <ListItem>Equity affects ownership and control</ListItem>
                                    <ListItem>Financing affects your credit profile, loan eligibility, and IRS reporting</ListItem>
                                </ul>
                                <Paragraph>Aligning your financing plan with: A solid business model, CPA-calculated cost of capital, Industry-specific NAICS coding, S Corp vs LLC tax structures, Wealth-building and exit planning ...can mean the difference between compounding success and compounding mistakes.</Paragraph>
                            </ContentBlock>

                            {/* Debt vs Equity: Real Example */}
                            <ContentBlock variants={fadeIn}>
                                <Heading><FiPieChart /> Debt vs Equity: Real Example</Heading>
                                <Table>
                                    <thead>
                                        <tr>
                                            <th>Scenario</th>
                                            <th>Equity Financing</th>
                                            <th>Debt Financing</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td>Capital Raised</td>
                                            <td>$100,000</td>
                                            <td>$100,000</td>
                                        </tr>
                                        <tr>
                                            <td>Cost</td>
                                            <td>25% ownership forever</td>
                                            <td>8% interest (repay $8,000/year)</td>
                                        </tr>
                                        <tr>
                                            <td>Business Profit Year 1</td>
                                            <td>$60,000</td>
                                            <td>$60,000</td>
                                        </tr>
                                        <tr>
                                            <td>Owner’s Retained Profit</td>
                                            <td>$45,000</td>
                                            <td>$52,000</td>
                                        </tr>
                                    </tbody>
                                </Table>
                                <Paragraph>Conclusion: Debt financing is often cheaper in the long term, if your business can handle the monthly payments.</Paragraph>
                            </ContentBlock>

                            {/* Why Ravline Corp? */}
                            <ContentBlock variants={fadeIn}>
                                <Heading><FiShield /> Why Ravline Corp?</Heading>
                                <Paragraph>Our Financing Services go beyond approvals — we design entire capital structures around:</Paragraph>
                                <ul>
                                    <ListItem>Business and Personal Credit Readiness</ListItem>
                                    <ListItem>Loan Package Design & Forecasting</ListItem>
                                    <ListItem>SBA & DSCR Strategy Integration</ListItem>
                                    <ListItem>Real Estate and Asset Leverage</ListItem>
                                    <ListItem>Equity Planning & Exit Strategy</ListItem>
                                    <ListItem>Legal Structuring & Trust-Based Planning</ListItem>
                                </ul>
                            </ContentBlock>

                            {/* Final Word */}
                            <ContentBlock variants={fadeIn}>
                                <Heading><FiCheck /> Final Word: Financial Power Is Strategic Power</Heading>
                                <Paragraph>Whether you're funding a business, scaling a real estate portfolio, or investing in generational wealth — your financing structure determines your tax exposure, profit margins, and exit options.</Paragraph>
                                <Paragraph>At Ravline, we help you:</Paragraph>
                                <ul>
                                    <ListItem>Choose the right financing path</ListItem>
                                    <ListItem>Reduce tax liability</ListItem>
                                    <ListItem>Retain ownership and control</ListItem>
                                    <ListItem>Build long-term value</ListItem>
                                </ul>
                                <Paragraph>Ready to Scale with Strategy? Talk to one of our consultants today and let’s build your custom Financial Growth Blueprint — tailored for your revenue, goals, and industry.</Paragraph>
                            </ContentBlock>
                        </motion.div>
                        )}

                        {activeSection === 'tax-strategies' && (
                        <motion.div variants={sectionVariants} initial="hidden" animate="visible">
                            <ContentBlock variants={fadeIn}>
                                <Heading>Tax Strategies: Ravline Playbook for Holding Real Estate</Heading>
                                <Paragraph>How you hold real estate directly impacts your taxes, liability, and long-term wealth. This playbook outlines key tax strategies and structures we use to help investors and business owners hold property more efficiently.</Paragraph>
                                <ContentImage src="/assets/images/tax-strategies-1.jpg" alt="Tax strategies for real estate holding structures" />
                            </ContentBlock>
                            <ContentBlock variants={fadeIn}>
                                <SubHeading>Entity Structure for Holding Real Estate</SubHeading>
                                <Paragraph>Holding property in the right entity can reduce liability and optimize taxes. Common structures include:</Paragraph>
                                <ul>
                                    <ListItem>LLCs (single- or multi-member) for flexibility and liability protection</ListItem>
                                    <ListItem>Holding companies to separate operating assets from real estate</ListItem>
                                    <ListItem>Trusts for estate planning and control</ListItem>
                                </ul>
                                <Paragraph>We help you choose the structure that fits your goals, state rules, and how you plan to use the property (rental, flip, or long-term hold).</Paragraph>
                            </ContentBlock>
                            <ContentBlock variants={fadeIn}>
                                <SubHeading>Depreciation & Cost Segregation</SubHeading>
                                <Paragraph>Real estate allows significant depreciation deductions. Cost segregation studies can accelerate depreciation by reclassifying components (e.g., fixtures, land improvements) into shorter-life categories, improving cash flow and reducing taxable income in early years.</Paragraph>
                            </ContentBlock>
                            <ContentBlock variants={fadeIn}>
                                <SubHeading>1031 Exchange & Deferring Gains</SubHeading>
                                <Paragraph>When selling investment or business-use property, a 1031 exchange lets you defer capital gains by reinvesting into like-kind replacement property. Proper timing, qualified intermediaries, and documentation are critical — we integrate this into your hold-and-sell strategy.</Paragraph>
                            </ContentBlock>
                            <ContentBlock variants={fadeIn}>
                                <SubHeading>Deducting Interest & Operating Expenses</SubHeading>
                                <Paragraph>Interest on loans used to acquire or improve rental property is generally deductible. So are ordinary operating expenses: maintenance, insurance, property management, and repairs. Keeping clear records and separating personal vs. business use supports maximum deductions and audit readiness.</Paragraph>
                                <ContentImage src="/assets/images/tax-strategies-2.jpg" alt="Real estate tax planning and deductions" />
                            </ContentBlock>
                            <ContentBlock variants={fadeIn}>
                                <SubHeading>Working With a CPA & Ravline</SubHeading>
                                <Paragraph>Tax strategy should be designed with a qualified CPA who understands real estate and your overall financial picture. At Ravline, we align entity structure, financing, and exit planning with your tax strategy — so you can hold real estate in a way that builds wealth and minimizes unnecessary tax exposure.</Paragraph>
                                <Paragraph>Ready to structure your holdings for tax efficiency? Talk to our team to integrate these strategies into your Ravline playbook.</Paragraph>
                            </ContentBlock>
                        </motion.div>
                        )}
                    </ContentContainer>
                </SectionWrapper>

                <Footer />
            </div>
        </div>
    );
}